import Phaser from 'phaser';
import { preloadActorManifest, prepareActorFromCache, THIRD_PIG, type PreparedActor } from '../anim/actor-clipset';
import { preloadNpcManifests, prepareNpcs } from '../anim/npc-clipset';
import {
  SCENE_REGISTRY_KEY,
  activeSceneId,
  prepareScene,
  sceneJsonKey,
  sceneUrl,
  sourcedScene,
} from '../scene/scene-registry';

// PreloadScene — the first of the two loader runs, and where the active LOCATION
// is chosen. It fetches two manifests: the actor's baked clip set (which names
// the part PNGs BootScene then loads) and the Scene record (which names the plate
// BootScene then loads). Both are validated here and published for the next Scene.
//
// Phaser runs one loader per Scene, between preload() and create(), so the
// manifest→assets dependency becomes an ordering of Scenes: this one fetches and
// validates the manifests; BootScene fetches the assets they name.

/** Where the prepared (parsed, validated, feet-origin-normalized) actor is
 *  published for BootScene to pick up. */
export const PREPARED_ACTOR_KEY = 'actor:prepared';

export class PreloadScene extends Phaser.Scene {
  private sceneId!: string;

  constructor() {
    super('Preload');
  }

  preload(): void {
    // Resolved here, not at construction: a taken exit restarts this Scene with
    // a new active location (scene-registry requestScene), same instance.
    this.sceneId = activeSceneId();
    this.load.json(sceneJsonKey(this.sceneId), sceneUrl(this.sceneId));
    // The actor is prepared once per run: prepareActor normalizes the cached
    // JSON in place, so a transition must reuse the first run's result rather
    // than re-normalizing (and re-fetching) it.
    if (!this.registry.get(PREPARED_ACTOR_KEY)) preloadActorManifest(this, THIRD_PIG);
    preloadNpcManifests(this);
  }

  create(): void {
    // An installed authoring source (Arcade Authoring, ?edit=1) outranks the
    // file: its in-memory record carries the session's edits, and the fetched
    // JSON is the stale copy on disk. No source (every runtime boot) → the
    // fetched record, exactly as before.
    const sourced = sourcedScene(this.sceneId);
    const rawScene = sourced ?? this.cache.json.get(sceneJsonKey(this.sceneId));
    if (!rawScene) throw new Error(`PreloadScene: scene '${this.sceneId}' failed to load (${sceneUrl(this.sceneId)})`);
    this.registry.set(SCENE_REGISTRY_KEY, prepareScene(rawScene));

    if (!this.registry.get(PREPARED_ACTOR_KEY)) {
      const prepared: PreparedActor = prepareActorFromCache(this, THIRD_PIG);
      this.registry.set(PREPARED_ACTOR_KEY, prepared);
    }
    prepareNpcs(this);
    this.scene.start('Boot');
  }
}
